/**
 * « Ce fil attend que l'avocat choisisse son papier à en-tête » : ce qui fait poser le voile de
 * choix (`components/CanvasDepartChoixVoile.tsx`) par-dessus une page neuve.
 *
 * Le fait vit hors de React et hors du scope documentaire parce qu'il parle d'un GESTE, pas
 * d'un document : le même document vierge est proposé au choix quand l'avocat vient de le
 * demander (bouton Canvas, « Nouveau »), et ne l'est pas quand l'agent ou un job vient de le
 * poser. Le loger dans la présentation ferait hériter le voile à quiconque remonte ou copie
 * cette présentation.
 *
 * Seuls les deux gestes humains qui donnent une page neuve le lèvent. Un chemin d'agent, de
 * job, de brouillon rouvert ou de Word importé ne l'appelle jamais : un montage de canvas est
 * borné (`CANVAS_MOUNT_WAIT_MS`) et ne peut pas attendre un clic.
 *
 * Ensemble indexé par conversation, purgé au changement de compte (`canvasEviction`).
 */

import * as React from 'react';

let filsEnAttente: ReadonlySet<string> = new Set();
const auditeurs = new Set<() => void>();

function publier(suivant: ReadonlySet<string>): void {
  filsEnAttente = suivant;
  for (const auditeur of [...auditeurs]) auditeur();
}

/** L'avocat vient d'obtenir une page neuve dans ce fil : lui proposer son papier à en-tête. */
export function proposerLeChoixDeDepart(conversationId: string): void {
  if (filsEnAttente.has(conversationId)) return;
  publier(new Set(filsEnAttente).add(conversationId));
}

/**
 * Le choix est fait, ou n'a plus d'objet. Idempotent : clore un fil qui n'attend rien ne
 * réveille personne, le voile l'appelant à chaque rendu où il n'a plus lieu d'être.
 */
export function cloreLeChoixDeDepart(conversationId: string): void {
  if (!filsEnAttente.has(conversationId)) return;
  const suivant = new Set(filsEnAttente);
  suivant.delete(conversationId);
  publier(suivant);
}

export function leChoixDeDepartEstPropose(conversationId: string | null): boolean {
  return conversationId !== null && filsEnAttente.has(conversationId);
}

/** Changement de compte : ces attentes appartiennent au compte qui part. */
export function oublierLesChoixDeDepart(): void {
  if (filsEnAttente.size === 0) return;
  publier(new Set());
}

/** Lecture React : un booléen, donc un instantané stable pour `useSyncExternalStore`. */
export function useChoixDeDepartPropose(conversationId: string | null): boolean {
  const subscribe = React.useCallback((auditeur: () => void) => {
    auditeurs.add(auditeur);
    return () => { auditeurs.delete(auditeur); };
  }, []);
  const lire = React.useCallback(() => leChoixDeDepartEstPropose(conversationId), [conversationId]);
  return React.useSyncExternalStore(subscribe, lire, lire);
}
